import React, { useState, useRef, useEffect } from "react";
import { Table, Button, Input, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";

const CustomTable = ({
  pagination = {},
  dataSource,
  columns,
  loading,
  handlePageChange = () => {},
  handleTabChange = () => {},
  onClickRow = () => {},
  onTableChange = () => {},
}) => {
  const pageSize = 15;
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);

  // Effect to calculate and send filtered data to parent
  useEffect(() => {
    if (dataSource && onTableChange) {
      let filteredData = dataSource;

      // Apply filters
      Object.keys(filteredInfo).forEach((key) => {
        if (filteredInfo[key]) {
          filteredData = filteredData.filter((record) => {
            const filterValue = filteredInfo[key][0];
            if (!filterValue) return true;

            let location = "";
            if (record?.city) location += `${record?.city}`;
            if (record?.stateProv) location += `, ${record?.stateProv}`;
            if (record?.countryName) location += `, ${record?.countryName}`;

            if (key === "location") {
              return location
                ? location
                    .toString()
                    .toLowerCase()
                    .includes(filterValue.toLowerCase())
                : false;
            } else {
              return record[key]
                ? record[key]
                    .toString()
                    .toLowerCase()
                    .includes(filterValue.toLowerCase())
                : false;
            }
          });
        }
      });

      onTableChange(filteredData);
    }
  }, [dataSource, filteredInfo, onTableChange]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    setFilteredInfo((prev) => ({
      ...prev,
      [dataIndex]: selectedKeys.length ? selectedKeys : null,
    }));
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    setSearchText("");
    setFilteredInfo((prev) => ({
      ...prev,
      [dataIndex]: null,
    }));
  };

  const getColumnSearchProps = (dataIndex, render) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters, dataIndex);
              confirm({
                closeDropdown: false,
              });
            }}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),

    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    onFilter: (value, record) => {
      let location = "";
      if (record?.city) location += `${record?.city}`;
      if (record?.stateProv) location += `, ${record?.stateProv}`;
      if (record?.countryName) location += `, ${record?.countryName}`;

      if (dataIndex === "location") {
        return location
          ? location.toString().toLowerCase().includes(value.toLowerCase())
          : "";
      } else {
        return record[dataIndex]
          ? record[dataIndex]
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase())
          : "";
      }
    },

    // render: (text) =>
    //   searchedColumn === dataIndex ? <span>{text}</span> : text,
    render: (text, record) => {
      let location = "";
      if (record?.city) location += `${record?.city}`;
      if (record?.stateProv) location += `, ${record?.stateProv}`;
      if (record?.countryName) location += `, ${record?.countryName}`;

      return (
        <>
          {dataIndex === "location" ? (
            <span>{location}</span>
          ) : dataIndex === "landingPage" ? (
            <a
              href={record[dataIndex]}
              rel="noreferrer"
              target="_blank"
              title={record[dataIndex]}
            >
              {record[dataIndex] ?? "-"}
            </a>
          ) : (
            <span>{record[dataIndex] ?? "-"}</span>
          )}
        </>
      );
    },
  });

  const updatedColumns = columns?.map((col) => {
    const { columnSearch, ...rest } = col;

    return {
      ...rest,
      ...(columnSearch ? getColumnSearchProps(col.dataIndex) : {}),
    };
  });

  return (
    <Table
      loading={loading}
      columns={updatedColumns}
      dataSource={dataSource}
      size="small"
      pagination={{
        current: pagination?.current,
        pageSize: pagination?.pageSize ?? pageSize,
        showTotal: (total, range) => {
          return (
            <span>
              Records{" "}
              <strong>
                {range[1]} / {total}
              </strong>{" "}
            </span>
          );
        },
        onChange: (page, pageSize) => {
          handlePageChange({ ...pagination, current: page, pageSize });
        },
      }}
      scroll={{
        x: 1200,
      }}
      // onChange={handleTabChange}
      onRow={(record) => {
        return {
          onClick: async (event) => onClickRow(record),
        };
      }}
    />
  );
};

export default CustomTable;
